import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { Breadcrumb, FinalCTA, SisterServices } from "../components/site/ServiceSections";
import { BLOG_BY_SLUG, BLOG_POSTS, BLOG_CATEGORIES } from "../data/blog";

// Renders inline **bold** markdown to <strong> within a paragraph string.
// Keeps everything else as plain text — no nested formatting, no links inside paragraphs.
function renderInlineMarkdown(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    const m = part.match(/^\*\*(.+)\*\*$/);
    if (m) return <strong key={i} className="font-bold text-ink">{m[1]}</strong>;
    return part;
  });
}

export default function BlogArticle() {
  const { slug } = useParams();
  const post = BLOG_BY_SLUG[slug];
  if (!post) return <Navigate to="/blog" replace />;

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug && p.category === post.category).slice(0, 3);
  const fallback = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);
  const recs = related.length ? related : fallback;
  const categoryLabel = BLOG_CATEGORIES.find((c) => c.id === post.category)?.label || "Article";

  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />

      {/* Hero */}
      <section className="relative pt-32 lg:pt-40 pb-12 bg-ink">
        <div className="mx-auto max-w-[900px] px-6 lg:px-10">
          <Breadcrumb current={categoryLabel} />
          <p className="text-xs uppercase tracking-wider2 text-ember mb-5 font-bold">{categoryLabel} · {post.readMin} min read</p>
          <h1 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)" }}>
            {post.title}
          </h1>
          <p className="mt-6 text-base md:text-xl text-bone/80 leading-relaxed">
            {post.dek}
          </p>
        </div>
      </section>

      {/* Hero image */}
      <section className="bg-ink">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
          <Photo slug={post.hero} alt={post.title} ratio="21/9" priority />
        </div>
      </section>

      {/* Article body */}
      <article className="bg-bone text-ink py-20 lg:py-28">
        <div className="mx-auto max-w-[760px] px-6 lg:px-10">
          {post.sections.map((s, i) => (
            <section key={i} className={i === 0 ? "" : "mt-14"} data-testid={`section-${i}`}>
              <p className="text-xs uppercase tracking-wider2 text-ember mb-4 font-bold">[ {String(i + 1).padStart(2, "0")} ]</p>
              <h2 className="font-display tracking-editorial leading-[1] mb-6" style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)" }}>
                {s.h}
              </h2>
              {s.body.map((p, j) => (
                <p key={j} className="text-base md:text-lg text-char leading-relaxed mb-5">
                  {renderInlineMarkdown(p)}
                </p>
              ))}
            </section>
          ))}

          {/* Inline CTA */}
          <div className="mt-16 p-7 md:p-10 bg-ink text-bone">
            <p className="text-xs uppercase tracking-wider2 text-ember mb-4 font-bold">Talk to a Melbourne photographer</p>
            <h3 className="font-display text-2xl md:text-3xl tracking-editorial leading-tight mb-5">
              Need a hand putting this into practice?
            </h3>
            <p className="text-bone/75 leading-relaxed mb-6 max-w-xl">
              We shoot menus, venues and campaigns for Melbourne hospitality — every week, all over the city. One call, one quote, one shoot day.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/get-a-quote"><CTAButton variant="primary" size="md">Get a Shoot Quote</CTAButton></Link>
              <a href="tel:0494756111"><CTAButton variant="ghost" size="md" arrow={false}>Call 0494 756 111</CTAButton></a>
            </div>
          </div>

          {/* FAQs */}
          {post.faqs?.length > 0 && (
            <section className="mt-16">
              <p className="text-xs uppercase tracking-wider2 text-ember mb-4 font-bold">FAQ</p>
              <h2 className="font-display tracking-editorial leading-[1] mb-6" style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)" }}>
                Common questions.
              </h2>
              <Accordion type="single" collapsible className="border-t border-ink/15">
                {post.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-b border-ink/15">
                    <AccordionTrigger className="text-left py-6 hover:no-underline group">
                      <div className="flex items-baseline gap-4 pr-4 flex-1">
                        <span className="font-sans font-bold text-sm text-ember tracking-tight shrink-0">{String(i + 1).padStart(2, "0")}</span>
                        <span className="font-display text-lg md:text-xl tracking-editorial leading-tight group-hover:text-ember transition-colors">{f.q}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 pl-12 pr-4">
                      <p className="text-base text-char leading-relaxed max-w-3xl">{f.a}</p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          )}
        </div>
      </article>

      {/* Related */}
      <section className="bg-ink text-bone py-20 lg:py-28 border-t border-bone/10">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <p className="text-xs uppercase tracking-wider2 text-ember mb-3 font-bold">Keep reading</p>
          <h2 className="font-display tracking-editorial leading-[0.95] mb-10" style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
            Related <span className="italic font-light text-ember">articles.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-bone/10 border border-bone/10">
            {recs.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-related-${p.slug}`}
                className="group bg-ink hover:bg-[#161613] transition-colors p-6 md:p-7 flex flex-col"
              >
                <Photo slug={p.hero} alt={p.title} ratio="4/3" sizes="(min-width: 1024px) 30vw, 100vw" />
                <p className="mt-5 text-[10px] uppercase tracking-wider2 font-bold text-ember">
                  {BLOG_CATEGORIES.find((c) => c.id === p.category)?.label} · {p.readMin} min
                </p>
                <h3 className="mt-3 font-display text-lg md:text-xl tracking-editorial leading-tight group-hover:text-ember transition-colors">
                  {p.title}
                </h3>
                <div className="mt-5 inline-flex items-center gap-2 text-[10px] uppercase tracking-wider2 font-bold text-ember">
                  Read <ArrowUpRight size={12} strokeWidth={2} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/blog"><CTAButton variant="ghost" size="md">Back to journal</CTAButton></Link>
          </div>
        </div>
      </section>

      <SisterServices keys={["menu", "ubereats", "cafe", "gbp"]} />
      <FinalCTA
        eyebrow="Final step · Hand-confirmed quotes"
        headline="Reading time over."
        italic="Time to shoot."
        intro="Most Melbourne metro briefs are quoted in under five minutes by phone."
        backdropSlug={post.hero}
      />
      <Footer />
    </div>
  );
}
